import { isObject as sweftIsObject } from "@app/common/utils";
import { evaluateSingleBobjWithFormulaList } from "@app/data/utils";
import { VanguardService } from "@app/services/vanguardService";
import { mergeWith, isObject as lodashIsObject } from "lodash";

export const generateTransientObject = ({ context, event, transientConfig, transientValueGetter }) => {
    const transientObject = {
        ...event.object,
        ...(event?.fieldBeingUpdated ?? {}),
        ...(event?.expectedUpdateResult ?? {}),
    };
    const { projectionAttributeList = [], entityFormulaPathMap = [] } = context || {};
    const formulaToEvaluateList = projectionAttributeList.reduce((formulaList, nextProjection) => {
        const projectionFormulaObj = entityFormulaPathMap?.[nextProjection] ?? null;
        if (!projectionFormulaObj) {
            return formulaList;
        }
        return [
            ...formulaList,
            projectionFormulaObj
        ];
    }, []);

    transientObject[transientConfig.transientProperty] = transientValueGetter(transientObject);
    if (transientConfig.evaluateTransientObject === false) {
        return transientObject;
    }

    return evaluateSingleBobjWithFormulaList({ bobj: transientObject, formulaList: formulaToEvaluateList });
};

export const fetchDependenciesIfMissing = async (context) => {
    const { manyRelationshipList = [] } = context;

    if (manyRelationshipList.length === 0) {
        return null;
    }

    const fetchPromises = manyRelationshipList.map(async (rel) => {
        const targetEntity = rel.relation;
        const targetField = rel.field;
        const foreignKeyArrayName = rel.mappedTo ? rel.mappedTo : null;

        try {
            const response = await VanguardService.data.getBusinessObjectsOfEntity({
                entity: targetEntity,
                projectionAttributeList: foreignKeyArrayName ? [foreignKeyArrayName] : [],
                schemaTreeExclusionList: [],
                relatedProjectionPaths: [],
                additionalJsonLogicQueryObject: null,
                receiveEvaluatedBobj: false,
                formulaForBackendEval: [],
                preFilterEntityMap: null,
                savedQueryId: null,
                additionalSavedQueryIdList: null
            });
            const records = Array.isArray(response) ? response : (response?.loadedData || []);
            return { field: targetField, data: records };
        } catch (error) {
            return { field: targetField, data: [] };
        }
    });

    const results = await Promise.all(fetchPromises);
    return results.reduce((map, item) => {
        map[item.field] = item.data; return map;
    }, {});
};

export const mapDependenciesToData = (dataList, context) => {
    const { relationshipDataMap = {}, manyRelationshipList = [] } = context;

    // Safety check in case XState passes an object instead of an array
    const safeDataList = Array.isArray(dataList) ? dataList : (dataList?.loadedData || []);

    if (!manyRelationshipList.length || Object.keys(relationshipDataMap).length === 0) {
        return safeDataList;
    }

    return safeDataList.map((row) => {
        const mappedRow = { ...row };
        const rowId = mappedRow.id;

        manyRelationshipList.forEach((rel) => {
            const targetField = rel.field;
            const foreignKeyArrayName = rel.mappedTo ? rel.mappedTo.split('.')[1] : null;

            if (relationshipDataMap[targetField] && foreignKeyArrayName) {
                const originalRelArray = Array.isArray(mappedRow[targetField]) ? mappedRow[targetField] : [];

                mappedRow[targetField] = originalRelArray.map(originalObj => {
                    const cachedObj = relationshipDataMap[targetField].find(c => c.id === originalObj.id);

                    if (cachedObj && Array.isArray(cachedObj[foreignKeyArrayName])) {
                        // 1. CLONE the array to break the shared memory reference!
                        const foreignKeyArray = [...cachedObj[foreignKeyArrayName]];

                        // 2. ENSURE the current rowId is present for optimistic UI additions
                        if (!foreignKeyArray.includes(rowId)) {
                            foreignKeyArray.push(rowId);
                        }

                        return {
                            ...originalObj,
                            [foreignKeyArrayName]: foreignKeyArray
                        };
                    }

                    return originalObj;
                });
            }
        });

        return mappedRow;
    });
};

export const generateTransientObjectList = ({ context, event, transientConfig, transientValueGetter }) => {
    const { objectList } = event;
    const { projectionAttributeList = [], entityFormulaPathMap = [] } = context || {};
    const formulaToEvaluateList = projectionAttributeList.reduce((formulaList, nextProjection) => {
        const projectionFormulaObj = entityFormulaPathMap?.[nextProjection] ?? null;
        if (!projectionFormulaObj) {
            return formulaList;
        }
        return [
            ...formulaList,
            projectionFormulaObj
        ];
    }, []);

    return objectList.map((object) => {
        const transientObject = object;
        transientObject[transientConfig.transientProperty] = transientValueGetter(transientObject);
        if (transientConfig.evaluateTransientObject === false) {
            return transientObject;
        }

        return evaluateSingleBobjWithFormulaList({ bobj: transientObject, formulaList: formulaToEvaluateList });
    });
};

const mergeBobjCustomizerWithKeyProperty = (keyProperty) => (newBobjValue, currentBobjValue) => {
    if (!lodashIsObject(newBobjValue) && !lodashIsObject(currentBobjValue)) {
        return newBobjValue;
    }
    const _newBobjValueIsObj = sweftIsObject(newBobjValue);
    const _currentBobjValueIsObj = sweftIsObject(currentBobjValue);

    if (_newBobjValueIsObj && _currentBobjValueIsObj) {
        // eslint-disable-next-line no-use-before-define
        return mergeBobj({ currentBobj: currentBobjValue, newBobj: newBobjValue, keyProperty });
    }

    if (_newBobjValueIsObj) {
        if (typeof _currentBobjValueIsObj === "string") {
            return newBobjValue;
        }
    } else if (typeof newBobjValue === "string") {
        return currentBobjValue;
    }

    const _newBobjValueIsArr = Array.isArray(newBobjValue);
    const _currentBojValueIsArr = Array.isArray(currentBobjValue);

    if (_newBobjValueIsArr && _currentBojValueIsArr) {
        return newBobjValue.map((newNestedObj) => {
            const currentNestedObj = currentBobjValue.find((obj) => newNestedObj && obj && newNestedObj[keyProperty] === obj[keyProperty]);
            if (!currentNestedObj) {
                return newNestedObj;
            }
            // eslint-disable-next-line no-use-before-define
            return mergeBobj({ currentBobj: currentNestedObj, newBobj: newNestedObj, keyProperty });
        });
    }

    return newBobjValue;
};

export const mergeBobj = ({ currentBobj, newBobj, keyProperty }) => {
    return mergeWith(newBobj, currentBobj, mergeBobjCustomizerWithKeyProperty(keyProperty));
};

export const getFormulaToEvaluateList = ({ projectionAttributeList = [], entityFormulaPathMap = {} } = {}) => projectionAttributeList.reduce((formulaList, nextProjection) => {
    const projectionFormulaObj = entityFormulaPathMap[nextProjection];
    if (!projectionFormulaObj) {
        return formulaList;
    }
    return [...formulaList, projectionFormulaObj];
}, []);

